import { useMutation } from '@apollo/react-hooks'
import type { Theme } from '@material-ui/core'
import { LinearProgress, makeStyles } from '@material-ui/core'
import CachedRoundedIcon from '@material-ui/icons/CachedRounded'
import CloseIcon from '@material-ui/icons/Close'
import type { AppDocsQuery } from '@paintscout/api'
import { AppDocsDocument, useUpdateUserMutation } from '@paintscout/api'
import type { DialogProps } from '@paintscout/ui'
import {
  Dialog,
  IconButton,
  LeaderboardTrophyIcon,
  useDialogs,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  RadioButtons,
  Skeleton,
  Typography,
  useUser
} from '@paintscout/ui'
import { gql } from 'apollo-boost'
import classNames from 'classnames'
import { Form, Formik } from 'formik'
import { useSnackbar } from 'notistack'
import type { ReactNode } from 'react'
import React, { useEffect, useState } from 'react'
import { useAuth } from 'react-auth0'
import { Sentry } from 'src/util/appSentry'
import { GamificationExitDialog } from './GamificationExitDialog'

const useStyles = makeStyles((theme: Theme) => ({
  dialogPaper: {
    [theme.breakpoints.down('xs')]: {
      margin: '18px'
    }
  },
  dialogTitle: {
    backgroundColor: theme.paintscout.palette.primary,
    fontSize: '2rem',
    color: theme.palette.common.white,
    padding: theme.spacing(1.5, 4),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1.5)
    }
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '1.1rem'
  },
  contentTitle: {
    fontSize: '30px',
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      fontSize: '24px'
    }
  },
  contentText: {
    fontSize: '18px',
    [theme.breakpoints.down('xs')]: {
      fontSize: '16px'
    }
  },
  leaderboardsIcon: {
    width: 30,
    height: 30,
    marginRight: theme.spacing(2)
  },
  dialogContent: {
    padding: theme.spacing(3, 4),
    paddingBottom: 0,
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(3, 2)
    }
  },
  dialogActions: {
    padding: theme.spacing(1.5, 4),
    height: 'fit-content',
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1.5, 2)
    }
  },
  startImg: {
    margin: '20px auto',
    display: 'block',
    maxWidth: '100%',
    maxHeight: 'calc(100vh - 450px)',
    minHeight: '250px',
    [theme.breakpoints.down('xs')]: {
      marginBottom: 0,
      minHeight: 'unset',
      margin: '20px -15px',
      maxWidth: 'calc(100% + 30px)'
    }
  },
  endImgAdmin: {
    margin: '20px auto',
    display: 'block',
    maxWidth: '53%',
    [theme.breakpoints.down('xs')]: {
      margin: '20px auto',
      maxWidth: '75%'
    }
  },
  endImgSales: {
    margin: '20px auto',
    display: 'block',
    maxWidth: '75%',
    [theme.breakpoints.down('xs')]: {
      margin: '20px auto',
      maxWidth: '100%'
    }
  },

  progressBarContainer: {
    padding: theme.spacing(2, 3),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2, 1)
    }
  },
  progressBarRoot: {
    backgroundColor: theme.paintscout.palette.brand.Neutral.P90,
    height: 11,
    borderRadius: 5,
    boxShadow: theme.shadows[1],
    [theme.breakpoints.down('xs')]: {
      height: 13
    }
  },
  progressBar: {
    backgroundColor: theme.palette.success.main,
    borderRadius: 5
  },
  iconContainer: {
    padding: theme.spacing(1),
    backgroundColor: theme.paintscout.palette.brand.Neutral.P94,
    borderRadius: 4,
    width: '45px',
    height: '45px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: theme.spacing(4)
  },
  uniqueNameContainer: {
    border: `1px solid ${theme.paintscout.palette.brand.Neutral.P50}`,
    borderRadius: 3,
    padding: theme.spacing(1, 1.5, 1.5, 1.5),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      marginBottom: theme.spacing(-2),
      padding: theme.spacing(1),
      border: `3px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
      borderRadius: 6
    }
  },
  uniqueName: {
    fontSize: '28px',
    [theme.breakpoints.down('xs')]: {
      fontSize: '20px'
    }
  },
  closeButton: {
    position: 'absolute',
    color: 'white',
    top: 4,
    right: 8,
    [theme.breakpoints.down('xs')]: {
      top: 3,
      right: 0
    }
  },
  regenerateButton: {
    color: theme.paintscout.palette.brand.Neutral.P35,
    fontSize: '0.9rem',
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.65rem'
    }
  },
  radioButtons: {
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(-1),
    marginLeft: theme.spacing(-1)
  },
  confirmButton: {
    background: theme.paintscout.palette.primary,
    color: theme.palette.common.white,
    '&:hover': {
      background: theme.paintscout.palette.primary
    }
  },
  link: {
    color: theme.paintscout.palette.primary
  },
  spacer: {
    height: theme.spacing(2)
  },
  mobileNegativeMargin: {
    [theme.breakpoints.down('xs')]: {
      marginBottom: theme.spacing(-2)
    }
  }
}))

interface GamificationLaunchDialogProps extends DialogProps {
  isAdmin: boolean
  isAutomaticOpen?: boolean
}

interface GamificationDialogPage {
  key: string
  content: ReactNode
  confirmButtonLabel: string
  confirmButtonDisabled?: boolean
  onCancel: () => void
}

interface FormValues {
  projectTypes: 'residential' | 'commercial' | 'residential-and-commercial'
}

export default function GamificationLaunchDialog(props: GamificationLaunchDialogProps) {
  const classes = useStyles()
  const { isAdmin, isAutomaticOpen } = props
  const { openDialog, dismissDialog, dismissAllDialogs } = useDialogs()
  const { enqueueSnackbar } = useSnackbar()
  const [page, setPage] = useState(0)
  const { user } = useUser()
  const [playerName, setPlayerName] = useState(user.user_metadata.playerName)
  const [playerNameLoading, setPlayerNameLoading] = useState(!playerName)
  const { authenticate } = useAuth()

  const [regeneratePlayerName] = useMutation(gql`
    mutation regeneratePlayerName($userId: String) {
      regeneratePlayerName(userId: $userId)
    }
  `)

  const [updateUser] = useUpdateUserMutation()

  const handleBlockLaunchDialog = async (hasJoinedLeaderboard: boolean) => {
    localStorage.setItem('blockLaunchDialog', 'true')
    const input = {
      user_id: user.user_id,
      user_metadata: {
        firstName: user.user_metadata.firstName,
        lastName: user.user_metadata.lastName,
        title: user.user_metadata.title,
        phoneNumber: user.user_metadata.phoneNumber,
        phoneExtension: user.user_metadata.phoneExtension?.trim(),
        customAvatar: user.user_metadata.customAvatar,
        blockLaunchDialog: true,
        playerName,
        hasJoinedLeaderboard
      }
    }
    await updateUser({
      variables: {
        input
      },
      context: {
        onlineOnly: true
      },
      update(cache, { data }) {
        const appDocs = cache.readQuery<AppDocsQuery>({
          query: AppDocsDocument
        })

        cache.writeQuery<AppDocsQuery>({
          query: AppDocsDocument,
          data: {
            ...appDocs,
            appDocs: {
              ...appDocs.appDocs,
              preferences: data.updateUser.preferences
            }
          }
        })
      }
    })
    await authenticate({ ignoreCache: true }).catch((e) => {
      console.error('Error while refreshing profile session:', e)
    })
  }

  useEffect(() => {
    if (!playerName) {
      regeneratePlayerName({ variables: { userId: user.user_id } }).then((res) => {
        setPlayerName(res.data.regeneratePlayerName)
        setPlayerNameLoading(false)
      })
    }
  }, [])

  const initialValues: FormValues = {
    projectTypes: null
  }

  async function handleUpdateProjectTypes(projectTypes: FormValues['projectTypes']) {
    try {
      const input = {
        user_id: user.user_id,
        user_metadata: {
          firstName: user.user_metadata.firstName,
          lastName: user.user_metadata.lastName,
          title: user.user_metadata.title,
          phoneNumber: user.user_metadata.phoneNumber,
          phoneExtension: user.user_metadata.phoneExtension?.trim(),
          customAvatar: user.user_metadata.customAvatar,
          blockLaunchDialog: true,
          playerName,
          projectTypes
        }
      }
      await updateUser({
        variables: {
          input
        },
        context: {
          onlineOnly: true
        },
        update(cache, { data }) {
          const appDocs = cache.readQuery<AppDocsQuery>({
            query: AppDocsDocument
          })

          cache.writeQuery<AppDocsQuery>({
            query: AppDocsDocument,
            data: {
              ...appDocs,
              appDocs: {
                ...appDocs.appDocs,
                preferences: data.updateUser.preferences
              }
            }
          })
        }
      })
      await authenticate({ ignoreCache: true }).catch((e) => {
        console.error('Error while refreshing profile session:', e)
      })
    } catch (error) {
      console.log(error)
      enqueueSnackbar('Something went wrong, please try again later', { variant: 'error' })
      dismissAllDialogs()
    }
  }

  const handlePopCallouts = () => {
    if (typeof BroadcastChannel != 'undefined') {
      const channel = new BroadcastChannel('callout')
      channel.postMessage('confirmed-dialog')
    }
  }

  async function handleSubmit() {
    await handleBlockLaunchDialog(true)
    handlePopCallouts()
    dismissAllDialogs()
  }

  const handleNextClick = () => {
    setPage((prevPage) => prevPage + 1)
  }

  const handleBackClick = () => {
    setPage((prevPage) => prevPage - 1)
  }

  const handlePreJoinCancel = () => {
    openDialog(GamificationExitDialog, {
      onCancel: dismissDialog,
      onConfirm: (blockDialog?: boolean) => {
        if (blockDialog) {
          handleBlockLaunchDialog(false)
        }
        dismissAllDialogs()
      },
      hasJoinedLeaderboard: false,
      allowDontShowMessage: isAutomaticOpen,
      playerName
    })
  }

  const handlePostJoinCancel = () => {
    openDialog(GamificationExitDialog, {
      onCancel: dismissDialog,
      onConfirm: async () => {
        await handleSubmit()
      },
      hasJoinedLeaderboard: true,
      playerName
    })
  }

  async function handleRegenerate() {
    setPlayerNameLoading(true)

    try {
      const res = await regeneratePlayerName({
        variables: {
          userId: user.user_id
        }
      })
      setPlayerName(res.data.regeneratePlayerName)
      // wait 2 seconds before setting loading to false
      // we don't want people to spam the button
      setTimeout(() => {
        setPlayerNameLoading(false)
      }, 2000)
    } catch (e) {
      console.error(e)
      enqueueSnackbar('Unable to regenerate player name', { variant: 'error' })
      Sentry.captureException(e)
      setPlayerNameLoading(false)
    }
  }

  return (
    <Dialog {...props} fullWidth maxWidth="sm" fullScreen={false} classes={{ paper: classes.dialogPaper }}>
      <Formik initialValues={initialValues} onSubmit={handleSubmit}>
        {({ values, handleSubmit: handleFormSubmit, setFieldValue, isSubmitting }) => {
          const pages: GamificationDialogPage[] = [
            {
              key: 'welcome',
              content: (
                <>
                  <Typography variant="h4" className={classes.contentTitle}>
                    Introducing PaintScout Leaderboards!
                  </Typography>
                  <Typography className={classes.contentText}>
                    <>
                      See how your sales perform against thousands of sales reps across PaintScout. The top 300 will be
                      ranked on <i>Sales</i> and <i>Win Rate</i>.
                    </>
                  </Typography>
                  <img
                    className={classes.startImg}
                    src={
                      isAdmin
                        ? 'https://paintscout.s3.us-east-1.amazonaws.com/gamification-launch-admin.png'
                        : 'https://paintscout.s3.us-east-1.amazonaws.com/gamification-launch-sales.png'
                    }
                  />
                </>
              ),
              confirmButtonLabel: 'Start',
              onCancel: handlePreJoinCancel
            },
            {
              key: 'privacy',
              content: (
                <>
                  <Typography variant="h4" className={classes.contentTitle}>
                    We Value Your Privacy
                  </Typography>
                  <Typography className={classes.contentText}>
                    We’ve designed the PaintScout Leaderboards to protect your privacy, so no personally identifiable
                    information is ever transferred or shown to users outside of your team.
                    <br />
                    <br />
                    As a PaintScout user you're automatically included in the Leaderboards, but you can opt-out at any
                    time by{' '}
                    <a href="mailto:support@paintscout.com" className={classes.link}>
                      contacting us
                    </a>
                    .
                    <br />
                    <br />
                    For more information about the Leaderboards and how PaintScout keeps your information safe, check
                    our{' '}
                    <a
                      href="https://www.paintscout.com/privacy-policy/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={classes.link}
                    >
                      Privacy Policy
                    </a>{' '}
                    and{' '}
                    <a
                      href="https://www.paintscout.com/terms-of-service/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={classes.link}
                    >
                      Terms of Use
                    </a>
                    .
                  </Typography>
                  <div className={classes.spacer} />
                </>
              ),
              confirmButtonLabel: 'Next',
              onCancel: handlePreJoinCancel
            },
            {
              key: 'accessing',
              content: (
                <>
                  <Typography variant="h4" className={classes.contentTitle}>
                    What type of projects do you sell?
                  </Typography>
                  <Typography className={classes.contentText}>
                    We’ll use this to help our algorithm determine your correct ranking.
                  </Typography>
                  <div className={classes.spacer} />

                  <RadioButtons
                    classes={{
                      root: classes.radioButtons,
                      label: classes.contentText
                    }}
                    options={[
                      { name: 'residential', label: 'Residential' },
                      { name: 'commercial', label: 'Commercial' },
                      { name: 'residential-and-commercial', label: 'Residential & Commercial' }
                    ]}
                    name="projectTypes"
                    value={values.projectTypes}
                    onChange={(value) => setFieldValue('projectTypes', value)}
                  />
                </>
              ),
              confirmButtonLabel: 'Next',
              confirmButtonDisabled: !values.projectTypes,
              onCancel: handlePreJoinCancel
            },
            {
              key: 'joined',
              content: (
                <Grid container justifyContent="center">
                  <Grid item xs={12}>
                    <Typography variant="h4" className={classes.contentTitle}>
                      Your Unique Anonymous Name
                    </Typography>
                  </Grid>
                  <Typography className={classes.contentText} style={{ marginBottom: '16px' }}>
                    Other players will only see this name, not your real one. You can change it anytime.
                  </Typography>
                  <Grid item className={classes.uniqueNameContainer}>
                    {playerNameLoading ? (
                      <div style={{ width: '320px' }}>
                        <Skeleton variant="text" width="100%" height={68} />
                      </div>
                    ) : (
                      <Grid container alignItems="center" justifyContent="center">
                        <Grid item xs={12}>
                          <Typography align="center" className={classes.uniqueName}>
                            {playerName}
                          </Typography>
                        </Grid>
                        <Button
                          onClick={handleRegenerate}
                          className={classes.regenerateButton}
                          size="small"
                          icon={CachedRoundedIcon}
                          variant="text"
                          flipIconSide
                          iconSize="large"
                        >
                          Regenerate
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              ),
              confirmButtonLabel: 'Next',
              confirmButtonDisabled: playerNameLoading,
              onCancel: handlePostJoinCancel
            },
            {
              key: 'explore',
              content: (
                <>
                  <Typography variant="h4" className={classes.contentTitle}>
                    Explore the Leaderboards
                  </Typography>
                  <Typography className={classes.contentText}>
                    {isAdmin
                      ? 'View how you compare against the entire PaintScout network, or compare your team on a company-exclusive leaderboard. '
                      : 'See where you stand and take a look at the top 10 ranked players.'}
                  </Typography>
                  <img
                    className={isAdmin ? classes.endImgAdmin : classes.endImgSales}
                    src={
                      isAdmin
                        ? 'https://paintscout.s3.us-east-1.amazonaws.com/gamification-explore-admin.png'
                        : 'https://paintscout.s3.us-east-1.amazonaws.com/gamification-explore-sales.png'
                    }
                  />
                  <Typography className={classNames(classes.contentText, classes.mobileNegativeMargin)}>
                    <a
                      href="http://help.paintscout.com/en/articles/10375453-what-are-the-paintscout-leaderboards"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={classes.link}
                    >
                      Click here
                    </a>{' '}
                    to learn more about how PaintScout Leaderboards work.
                  </Typography>
                </>
              ),
              confirmButtonLabel: `Let's Go!`,
              onCancel: () => handleFormSubmit()
            }
          ]
          const currentPage = pages[page]
          return (
            <Form id="gamification-launch" style={{ display: 'contents' }}>
              <DialogTitle
                classes={{ root: classes.dialogTitle }}
                rightContent={
                  <IconButton disabled={isSubmitting} className={classes.closeButton} onClick={currentPage.onCancel}>
                    <CloseIcon />
                  </IconButton>
                }
              >
                <Typography variant="body1" className={classes.title}>
                  <LeaderboardTrophyIcon className={classes.leaderboardsIcon} />
                  Leaderboards
                </Typography>
              </DialogTitle>
              <DialogContent classes={{ root: classes.dialogContent }}>{currentPage.content}</DialogContent>
              <div className={classes.progressBarContainer}>
                <LinearProgress
                  classes={{ root: classes.progressBarRoot, bar: classes.progressBar }}
                  variant="determinate"
                  value={((page + 1) / pages.length) * 100}
                />
              </div>
              <DialogActions
                leftButton={
                  page > 0 && (
                    <Button disabled={isSubmitting} variant="textNoPadding" onClick={handleBackClick}>
                      Back
                    </Button>
                  )
                }
                classes={{ root: classes.dialogActions }}
              >
                <Button
                  onClick={() => {
                    if (currentPage.key === 'explore') {
                      handleFormSubmit()
                    } else if (currentPage.key === 'accessing') {
                      handleUpdateProjectTypes(values.projectTypes)
                      handleNextClick()
                    } else {
                      handleNextClick()
                    }
                  }}
                  disabled={isSubmitting || currentPage.confirmButtonDisabled}
                  loading={isSubmitting}
                  className={classes.confirmButton}
                >
                  {currentPage.confirmButtonLabel}
                </Button>
              </DialogActions>
            </Form>
          )
        }}
      </Formik>
    </Dialog>
  )
}
